import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { useGridApiRef, gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid-pro';
import { Checkbox, Box, Tooltip } from '@mui/material';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import { useSelector } from '../../../../../redux/store';

RolesValueTable.propTypes = {
  roleType: PropTypes.object,
  tempRoles: PropTypes.array,
  setTempRoles: PropTypes.func,
  addUserFacilityOption: PropTypes.bool,
  isTemplate: PropTypes.bool,
};
const roleValuesDependencies = {
  18: 19, //EditInvoice:ViewInvoice
  64: 63, //EditManualInvoice:ViewManualInvoice
};
const editValues = Object.keys(roleValuesDependencies);
const viewValues = Object.values(roleValuesDependencies);

export default function RolesValueTable({ roleType, tempRoles, setTempRoles, addUserFacilityOption, isTemplate }) {
  const apiRef = useGridApiRef();
  const {
    data: { user },
  } = useSelector((state) => state.users);
  const { roleValues } = user;
  const { optionValues, innerOptionValues, id, linkedPermissionWithNoSuboptions } = roleType;
  const usesFacility = roleType?.useModel === 'Facility';

  const [allValues, setAllValues] = useState([]);
  const [rows, setRows] = useState(null);
  const [disabledMainKeys, setDisabledMainKeys] = useState([]);

  const isEditPermission = editValues.some((key) => Number(key) === roleType.id);
  const isViewPermission = viewValues.some((key) => Number(key) === roleType.id);
  const editRole = editValues.find((key) => roleValuesDependencies[key] === roleType?.id);

  useEffect(() => {
    setTempRoles(roleValues);
  }, [roleValues]);

  const tempRoleIsActive = tempRoles?.reduce((result, role) => {
    result[`${role?.userPermissionTypeId}|${role?.modelId}|${role?.modelAttachmentId ?? 0}`] = true;
    return result;
  }, {});
  const roleIsActive = roleValues?.reduce((result, role) => {
    result[`${role?.userPermissionTypeId}|${role?.modelId}|${role?.modelAttachmentId ?? 0}`] = true;
    return result;
  }, {});
  useEffect(() => {
    if (isViewPermission && rows) {
      const editCheckedModels = roleValues?.filter((role) => role.userPermissionTypeId === Number(editRole));
      const newDisabledKeys = rows
        ?.filter((row) => {
          const { modelId } = splitRowKey(row.mainKey);
          return editCheckedModels.some((model) => model.modelId === modelId);
        })
        .map((row) => row.mainKey);

      setDisabledMainKeys([...disabledMainKeys, ...newDisabledKeys]);
    }
  }, [isViewPermission, disabledMainKeys, roleValues, rows, editRole]);

  useEffect(() => {
    const addUserFacilityOptions = addUserFacilityOption && usesFacility && optionValues && innerOptionValues;
    const newOptionValues = addUserFacilityOptions
      ? [{ key: -1, value: "User's Facility" }, ...optionValues]
      : optionValues;
    const fullOptionValues = innerOptionValues
      ? newOptionValues?.flatMap((option) => innerOptionValues?.map((innerOption) => ({ option, innerOption })))
      : newOptionValues?.map((option) => ({ option }));
    setAllValues(fullOptionValues);
    const activeRoles = roleValues?.reduce((result, role) => {
      result[`${role?.userPermissionTypeId}|${role?.modelId}|${role?.modelAttachmentId ?? 0}`] = true;
      return result;
    }, {});
    setRows(
      (
        fullOptionValues?.map(({ option, innerOption }, index) => ({
          id: index,
          mainKey: innerOption?.key === -1 ? null : `${id}|${option.key}|${innerOption?.key ?? 0}`,
          mainOption: option.value,
          mainOptionKey: option.key,
          subOption: innerOption ? innerOption.value : '',
          optionSubOptions: option?.subOptions ?? [],
          innerOptionSubOptions: innerOption?.subOptions ?? [],
          description: innerOption ? innerOption.description : option?.description,
          subkeys: (innerOption?.subOptions ?? [])
            .filter((subOpt) => subOpt?.userPermissionTypeId)
            .map((subOpt) => `${subOpt?.userPermissionTypeId}|${option.key}|${subOpt.key}`),
          linkedKeys: linkedPermissionWithNoSuboptions?.map(linkedKey => `${linkedKey?.userPermissionTypeId}|${option.key}`),
          category: innerOption ? innerOption.value : '',
        })) ?? []
      )
        .map((row) => ({
          ...row,
          sortBy: `${
            (isTemplate && row.mainOptionKey === -1) ||
            (!isTemplate && usesFacility && row.mainOption === user.facility)
              ? '0'
              : '1'
          }${row.mainKey && activeRoles[row.mainKey] ? '0' : '1'}
      ${row.subkeys.some((key) => activeRoles[key]) ? '0' : '1'}${row.mainOption}`,
        }))
        .sort((row, nextRow) => {
          const sortByLowerCase = row.sortBy.toLowerCase();
          const nextSortByLowerCase = nextRow.sortBy.toLowerCase();
          return (sortByLowerCase > nextSortByLowerCase) - (sortByLowerCase < nextSortByLowerCase);
        })
    );
  }, [optionValues, innerOptionValues, id, usesFacility]);

  const splitRowKey = (rowId) => {
    const parts = rowId.split('|');
    const id = parts[0];
    const option = parts[1];
    const innerOption = parts[2] !== 'undefined' ? parts[2] : undefined;
    return {
      userPermissionTypeId: parseInt(id, 10),
      modelId: parseInt(option, 10),
      modelAttachmentId: parseInt(innerOption, 10) || undefined,
    };
  };

  const handleCheck = (event, rowId) => {
    event.stopPropagation();
    event.preventDefault();
    const splittedRowKey = splitRowKey(rowId);
    const { userPermissionTypeId } = splittedRowKey;
    const derivedKey = isEditPermission
      ? `${roleValuesDependencies[userPermissionTypeId]}|${splittedRowKey.modelId}|${
          splittedRowKey.modelAttachmentId ?? 0
        }`
      : undefined;
    const derivedRole = isEditPermission ? splitRowKey(derivedKey) : {};

    const roleIndex = tempRoles.findIndex(
      (role) => `${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}` === rowId
    );
    if (isEditPermission) {
      if (roleIndex !== -1) {
        setTempRoles(
          tempRoles.filter((role) => {
            const isEditingTargetRole =
              `${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}` === rowId;
            const isDerivedViewRole =
              `${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}` === derivedKey;
            return (!isEditingTargetRole && !isDerivedViewRole) || (isDerivedViewRole && !!roleIsActive[derivedKey]);
          })
        );
      } else {
        setTempRoles([
          ...tempRoles,
          splittedRowKey,
          ...(!tempRoles.some(
            (role) => `${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}` === derivedKey
          )
            ? [derivedRole]
            : []),
        ]);
      }
    } else {
      setTempRoles(
        roleIndex !== -1
          ? tempRoles.filter(
              (role) => `${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}` !== rowId
            )
          : [...tempRoles, splittedRowKey]
      );
    }
  };

  const handleCheckAll = (event, col = null, linkedPermission = false) => {
    event.stopPropagation();
    event.preventDefault();
    const visibleRowIds = gridFilteredSortedRowIdsSelector(apiRef?.current?.state);
    const userPermissionTypeId = linkedPermission ? col : col
      ? (
          allValues?.flatMap((val) =>
            val.innerOption?.subOptions?.filter((opt) => opt.header === col).map((opt) => opt.userPermissionTypeId)
          ) ?? []
        ).find(() => true)
      : null;
    const visibleRows = rows?.filter((row) => visibleRowIds.includes(row.id));
    const keys = userPermissionTypeId
      ? linkedPermission ? visibleRows.map((row) => `${userPermissionTypeId}|${row.mainOptionKey}|0`) :
        visibleRows.flatMap((row) => row.subkeys).filter((key) => key.startsWith(`${userPermissionTypeId}|`))
      : visibleRows.map((row) => row.mainKey).filter((key) => key);
    const derivedViewKeys = isEditPermission
      ? keys.map((key) => {
          const { modelId, modelAttachmentId } = splitRowKey(key);
          return `${roleValuesDependencies[roleType.id]}|${modelId}|${modelAttachmentId ?? 0}`;
        })
      : [];

    if (keys.every((key) => tempRoleIsActive[key])) {
      if (isViewPermission) {
        setTempRoles(
          tempRoles.filter(
            (role) =>
              !keys
                .filter((key) => !disabledMainKeys.includes(key))
                .includes(`${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}`)
          )
        );
      } else if (isEditPermission) {
        const inActiveDerivedViewKeys = derivedViewKeys.filter((key) => !roleIsActive[key]);

        setTempRoles(
          tempRoles.filter(
            (role) =>
              !keys.includes(`${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}`) &&
              !inActiveDerivedViewKeys.includes(
                `${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}`
              )
          )
        );
      } else {
        setTempRoles(
          tempRoles.filter(
            (role) => !keys.includes(`${role.userPermissionTypeId}|${role.modelId}|${role.modelAttachmentId ?? 0}`)
          )
        );
      }
      return;
    }
    if (isEditPermission) {
      setTempRoles([
        ...tempRoles,
        ...keys.filter((key) => !roleIsActive[key]).map((key) => splitRowKey(key)),
        ...derivedViewKeys.filter((key) => !roleIsActive[key]).map((key) => splitRowKey(key)),
      ]);
    } else {
      setTempRoles([...tempRoles, ...keys.filter((key) => !tempRoleIsActive[key]).map((key) => splitRowKey(key))]);
    }
  };

  const columns = useMemo(() => {
    const hasSubOptions = allValues?.some((val) =>
      val.innerOption?.subOptions?.some((innerVal) => innerVal.userPermissionTypeId)
    );
    return [
      {
        field: 'mainOption',
        headerName: roleType.modelLabel,
        type: 'customText',
        flex: 1,
        renderCell: (params) => (
          <Box>
            <Box
              sx={{
                color:
                  (isTemplate && params.row.mainOptionKey === -1) ||
                  (usesFacility && !isTemplate && user.facility === params.row.mainOption)
                    ? 'info.main'
                    : 'default',
              }}
            >
              {params.row.mainOption}
            </Box>
            {params.row.description && (
              <Box sx={{ fontSize: 12, color: (theme) => theme.palette.text.secondary }}>{params.row.description}</Box>
            )}
          </Box>
        ),
      },
      ...Array.from(
        new Set(
          (allValues?.flatMap((val) => val.option?.subOptions?.map((opt) => opt.header)) ?? []).filter((col) => col)
        )
      ).map((col) => ({
        field: `extraMainOpt-${col.replace(' ', '-')}`,
        type: 'customText',
        headerName: col,
        flex: 1,
        valueGetter: (params) => params.row.optionSubOptions.find((subOpt) => subOpt.header === col)?.value,
        renderCell: (params) => params.row.optionSubOptions.find((subOpt) => subOpt.header === col)?.value,
      })),
      ...(linkedPermissionWithNoSuboptions ?? []).map(col => ({        
        field: `linkedPermission-${col.header.replace(' ', '-')}`,
        flex: 1,
        headerName: col.header,
        renderCell: (params) => (
          <>
            <Checkbox
              checked={tempRoleIsActive[`${col.userPermissionTypeId}|${params.row.mainOptionKey}|0`] || false}
              onChange={(event) => handleCheck(event, `${col.userPermissionTypeId}|${params.row.mainOptionKey}|0`)}
            />
            {params.row?.mainOption}
          </>
        ),
        type: 'customText',
        checkBox: () => <Checkbox
              checked={rows?.filter((row) => row.mainKey).every((row) => tempRoleIsActive[`${col.userPermissionTypeId}|${row.mainOptionKey}|0`]) ?? false}
              sx={{ mr: 1, color: '#fff' }}
              onClick={(event) => handleCheckAll(event, col.userPermissionTypeId, true)}
            />,
      })),
      ...Array.from(new Set((allValues?.flatMap((val) => val.innerOption?.subOptions?.map((opt) => opt.header)) ?? []).filter((col) => col))).map((col) => ({
        field: `extraSubOpt-${col.replace(' ', '-')}`,
        flex: 1,
        headerName: col,
        valueGetter: (params) => params.row.innerOptionSubOptions.find((subOpt) => subOpt.header === col)?.value,
        checkBox: () => {
          if (!allValues.some((val) => val.innerOption?.subOptions.some((innerVal) => innerVal.header === col && innerVal.userPermissionTypeId)))
            return col;
          const userPermissionTypeId = (
            allValues?.flatMap((val) =>
              val.innerOption?.subOptions?.filter((opt) => opt.header === col).map((opt) => opt.userPermissionTypeId)
            ) ?? []
          ).find(() => true);
          const allChecked =
            rows
              ?.flatMap((row) => row.subkeys)
              .filter((key) => key.startsWith(`${userPermissionTypeId}|`))
              .every((key) => tempRoleIsActive[key]) ?? false;
          return (
            <Checkbox
              checked={allChecked}
              sx={{ mr: 1, color: '#fff' }}
              onClick={(event) => handleCheckAll(event, col)}
            />
          );
        },
        type: 'customText',
        renderCell: (params) => {
          const subOpt = params.row.innerOptionSubOptions.find((subOpt) => subOpt.header === col);
          const key = subOpt ? `${subOpt.userPermissionTypeId}|${params.row.mainOptionKey}|${subOpt.key}` : null;
          return (
            <>
              {subOpt?.userPermissionTypeId && (
                <Checkbox checked={tempRoleIsActive[key] || false} onChange={(event) => handleCheck(event, key)} />
              )}
              {subOpt?.value}
            </>
          );
        },
      })),
      ...(allValues?.some(({ innerOption }) => innerOption)
        ? [
            {
              field: 'subOption',
              flex: 1,
              type: 'customText',
              renderCell: (params) => (
                <>
                  {hasSubOptions && params.row.mainKey && (
                    <Checkbox
                      checked={tempRoleIsActive[params.row.mainKey] || false}
                      onChange={(event) => handleCheck(event, params.row.mainKey)}
                    />
                  )}
                  {params.row?.subOption}
                </>
              ),
              checkBox: () =>
                hasSubOptions ? (
                  <Checkbox
                    checked={rows?.filter((row) => row.mainKey).every((row) => tempRoleIsActive[row.mainKey]) ?? false}
                    sx={{ mr: 1, color: '#fff' }}
                    onClick={(event) => handleCheckAll(event)}
                  />
                ) : (
                  ''
                ),
              headerName: roleType.modelAttachmentLabel,
            },
          ]
        : []),
      ...(!hasSubOptions
        ? [
            {
              field: 'active',
              headerName: 'Active',
              flex: 1,
              renderHeader: () => (
                <>
                  <Checkbox
                    checked={rows?.filter((row) => row.mainKey).every((row) => tempRoleIsActive[row.mainKey]) ?? false}
                    sx={{ mr: 1, color: '#fff' }}
                    onClick={(event) => handleCheckAll(event)}
                  />
                  Active
                </>
              ),
              renderCell: (params) => (
                <Tooltip
                  title={
                    disabledMainKeys.includes(params.row.mainKey)
                      ? 'The view role cannot be deactivated while edit role is active.'
                      : ''
                  }
                >
                  <span>
                    <Checkbox
                      checked={tempRoleIsActive[params.row.mainKey] || false}
                      onChange={(event) => handleCheck(event, params.row.mainKey)}
                      disabled={disabledMainKeys.includes(params.row.mainKey)}
                    />
                  </span>
                </Tooltip>
              ),
              valueGetter: (params) =>
                tempRoleIsActive[params.row.mainKey]
                  ? `${
                      usesFacility &&
                      ((isTemplate && params.row.value === -1) ||
                        (!isTemplate && params.row.mainOption === user.facility))
                        ? '1'
                        : '0'
                    }1${params.row.label}`
                  : `${
                      usesFacility &&
                      ((isTemplate && params.row.value === -1) ||
                        (!isTemplate && params.row.mainOption === user.facility))
                        ? '1'
                        : '0'
                    }0${params.row.mainOption || params.row.company || params.row.subOption}`,
            },
          ]
        : []),
    ];
  }, [allValues, tempRoleIsActive, user.facility, rows]);
  return rows && rows.length > 0 ? (
    <CustomDataGrid
      gridId={`${roleType.id}-admin-roles-modal`}
      data={rows ?? []}
      gridColumns={columns}
      disableSelectionOnClick
      apiRef={apiRef}
      forceReRender={tempRoles}
    />
  ) : (
    <div style={{ textAlign: 'center' }}>
      There is no {roleType.label} set up for this {roleType?.useModel}.
    </div>
  );
}
